import React, {useCallback, useEffect, useState, useMemo} from 'react';
import {ActivityIndicator, StyleSheet, View} from 'react-native';
import _ from 'lodash';
import {Colors, Typography} from 'src/styles';
import {Target} from 'src/models';
import TrialByTrialProgressGraph from './graph';
import TrialByTrialTable from './table';
import {RHSeparator} from 'src/common-components/custom-ui-helpers';
import SelectInput from 'src/hook-form/select-input';
import ViewShot from 'react-native-view-shot';
import {Menu} from 'src/design-system';
import AntDesign from 'react-native-vector-icons/AntDesign';
import {SYNC_SERVER} from 'react-native-dotenv';
import AsyncStorage from '@react-native-async-storage/async-storage';
import moment from 'moment';
import {exportTrialByTrial} from 'src/common-utils/program-evaluation';
import {exportCsv} from 'src/common-utils/export-csv';
import {endOfDay, startOfDay} from 'date-fns';

const TrialByTrialProgress = ({
  program = {},
  analysis = {},
  sets = [],
  events = [],
  recentSets = [],
  recentEvents = [],
  sessions = [],
  programSessions = [],
  envs = [],
  targets = [],
  currentTarget,
  setCurrentTarget,
  table = false,
  graph = true,
  showGraphs = true,
  title = null,
  extras = {},
  startDate = '',
  endDate = '',
  sessionMessage = false,
  byDay = false,
  filteredEnvItems = [],
  shouldShowSummary = false,
  summaryOnly = false,
  shouldShowMenu = true,
  reportScreen = false,
  useObservables = false,
  session = null,
  patient = null,
}: any) => {
  const dropdownTargets = targets.map((_target: Target) => {
    return {
      label: _target.name,
      value: _target.id,
    };
  });

  const [programData, setData] = useState(null);
  const [programSets, setSets] = useState(sets);
  const [programEvents, setProgramEvents] = useState(events);
  const [filteredSets, setRecentSets] = useState(recentSets);
  const setsByTargetId = reportScreen
    ? _.groupBy(programSets, 'target_id')
    : _.groupBy(programSets, 'target.id');
  const recentSetsByTargetId = reportScreen
    ? _.groupBy(programSets, 'target_id')
    : !useObservables
    ? _.groupBy(recentSets, 'target_id')
    : _.groupBy(recentSets, 'target.id');

  const setsForTarget = useMemo(
    () => recentSetsByTargetId?.[currentTarget] || [],
    [currentTarget, recentSetsByTargetId],
  );
  const eventsForExport = sessionMessage ? recentEvents : programEvents;

  const tableSetsForCsv = useMemo(() => {
    if (!reportScreen) {
      return setsForTarget;
    }

    const start = startOfDay(new Date(startDate)).getTime();
    const end = endOfDay(new Date(endDate)).getTime();

    return (setsForTarget || []).filter((s: any) => {
      const d = Number(s?.date ?? 0);
      return d >= start && d <= end;
    });
  }, [reportScreen, setsForTarget, startDate, endDate]);

  const tableSetsForCsvSorted = useMemo(() => {
    const copy = [...(tableSetsForCsv || [])];
    if (reportScreen) {
      // table filters on set.date, so sort by set.date
      return copy.sort(
        (a: any, b: any) => Number(a?.date ?? 0) - Number(b?.date ?? 0),
      );
    }
    // non-report screens: fall back to start/end/session timestamps
    const ts = (s: any) =>
      Number(
        s?.startTimestamp ??
          s?.start_timestamp ??
          s?.session_date ??
          s?.date ??
          0,
      );
    return copy.sort((a, b) => ts(a) - ts(b));
  }, [tableSetsForCsv, reportScreen]);

  const buildTbtCsv = useCallback(async () => {
    return exportTrialByTrial({
      sets: tableSetsForCsvSorted,
      events: eventsForExport,
      useObservables,
      reportScreen,
      targetId: currentTarget,
      trialCount: program?.numberOfTrials,
      session,
      startDate,
      endDate,
    });
  }, [
    tableSetsForCsvSorted,
    eventsForExport,
    useObservables,
    reportScreen,
    currentTarget,
    program?.numberOfTrials,
    session,
    startDate,
    endDate,
  ]);

  const tableOptionsWithCsv = useMemo(() => {
    const base = extras?.tableOptions || [];
    return base.map((opt: any) => {
      const isCsv =
        (typeof opt.title === 'string' && opt.title.toLowerCase() === 'csv') ||
        opt.icon === 'file-excel';
      if (!isCsv) {
        return opt;
      }

      return {
        ...opt,
        onPress: async () => {
          const data = await buildTbtCsv();
          const datePart = session
            ? moment(session.sessionDate).format('YYYY-MM-DD')
            : [startDate, endDate]
                .map(d => moment(d).format('YYYY-MM-DD'))
                .join('_');

          const fileName = `${patient?.lastName ?? 'Patient'}_${
            program?.name
          }_${datePart}`;

          await exportCsv(fileName, data);
        },
      };
    });
  }, [
    extras?.tableOptions,
    buildTbtCsv,
    patient?.lastName,
    program?.name,
    startDate,
    endDate,
    session,
  ]);

  const eventsBySetId =
    reportScreen || !useObservables
      ? _.groupBy(programEvents, 'set_id')
      : _.groupBy(programEvents, 'set.id');
  const targetById = _.groupBy(targets, 'id');
  const sessionsByDay = _.groupBy(sessions, 'sessionDate');
  const [isLoading, setIsLoading] = useState(false);
  useEffect(() => {
    setCurrentTarget(targets?.[0]?.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [program]);
  const analyzeSets = useCallback(
    (targetId: string) => {
      const trialAnalysis: any = {};
      const analysisState: any = [];
      const dailyAnalysisState: any = [];
      const dailyAnalysis: any = {};
      if (targetId) {
        const sessionSets = _.groupBy(setsByTargetId[targetId], 'session.id');
        for (const sessionId of Object.keys(sessionSets)) {
          const sessionSet = sessionSets[sessionId];
          const sessionDate = sessions.find(
            (sesh: any) => sesh.id === sessionId,
          )?.sessionDate;
          let collectorIds: string[] = [];
          const avg = Math.ceil(
            sessionSet.reduce((totalSum: number, set: any) => {
              const setEvents = eventsBySetId[set?.id];
              let eventCollectors = _.uniq(
                _.map(setEvents, (event: any) => event?.createdBy),
              ).filter((id: string) => id !== '');
              collectorIds.push(...eventCollectors);
              return (
                totalSum +
                Math.ceil(
                  (setEvents?.filter((event: any) => event.value === 'success')
                    .length /
                    setEvents?.length) *
                    100,
                )
              );
            }, 0) / sessionSet.length,
          );
          const average = isNaN(avg) ? 0 : avg;
          const dailySessions = sessionsByDay[sessionDate];
          const sessionIds = _.map(dailySessions, 'id');
          const dailySets = setsByTargetId[targetId].filter((set: any) =>
            sessionIds.includes(set?.sessionId),
          );
          const dailyAvg = Math.ceil(
            dailySets.reduce((totalSum: number, set: any) => {
              const setEvents = eventsBySetId[set?.id];
              return (
                totalSum +
                Math.ceil(
                  (setEvents?.filter((event: any) => event.value === 'success')
                    .length /
                    setEvents?.length) *
                    100,
                )
              );
            }, 0) / dailySets.length,
          );
          const dailyAverage = isNaN(dailyAvg) ? 0 : dailyAvg;
          collectorIds = _.uniq(collectorIds);
          const sessionObject = {
            sessionDate,
            dailyAverage,
            sessionId,
            average,
            state: targetById[targetId][0]?.phase,
            id: sessionId,
            startTimestamp: moment(sessionDate).startOf('day').valueOf(),
            collectors: collectorIds,
          };
          analysisState.push(sessionObject);
          trialAnalysis[sessionId] = sessionObject;
        }
        const targetDailyAnalysis = _.groupBy(analysisState, 'sessionDate');
        for (const sessionDay of Object.values(targetDailyAnalysis)) {
          const dailySessionObject = {
            sessionDate: sessionDay[sessionDay.length - 1]?.sessionDate,
            sessionId: sessionDay[sessionDay.length - 1]?.sessionId,
            average: sessionDay[sessionDay.length - 1]?.dailyAverage,
            timestamp: sessionDay[sessionDay.length - 1]?.date,
            startTimestamp: sessionDay[sessionDay.length - 1]?.startTimestamp,
            collectors: sessionDay[sessionDay.length - 1]?.collectors,
            state: sessionDay[sessionDay.length - 1]?.state,
          };
          dailyAnalysisState.push(dailySessionObject);
          dailyAnalysis[sessionDay[sessionDay.length - 1]?.sessionDate] =
            dailySessionObject;
        }
      }

      return byDay ? dailyAnalysis : trialAnalysis;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setsByTargetId, eventsBySetId, program],
  );

  useEffect(() => {
    if (sessionMessage === false) {
      const analyzeMobilePrograms = async () => {
        setIsLoading(true);
        try {
          const response = await fetch(
            `${SYNC_SERVER}/calculate-reports?programId=${program.id}` +
              (currentTarget ? `&targetId=${currentTarget}` : ''),
            {
              method: 'GET',
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: `Bearer ${await AsyncStorage.getItem(
                  'accessToken',
                )}`,
              },
            },
          );

          if (response.status === 200) {
            const value = await response.json();
            setData(value?.analysis);
            setSets(value?.sets);
            setProgramEvents(value?.events);
            setRecentSets(
              value?.sets
                ? value?.sets.filter(
                    (set: any) =>
                      set?.date >= new Date(startDate).getTime() &&
                      set?.date <= new Date(endDate).getTime(),
                  )
                : [],
            );
          } else {
            setData(null);
          }
        } catch (e) {
          console.log('fetch error:', e);
          setData(null);
        } finally {
          setIsLoading(false);
        }
      };

      analyzeMobilePrograms();
    } else if (!useObservables) {
      setData(analysis[targetById?.[currentTarget]?.[0]?.id]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTarget]);

  return (
    <>
      {isLoading ? (
        <>
          <ActivityIndicator size={'large'} style={[styles.pt_20]} />
        </>
      ) : (
        <View style={showGraphs ? styles.mt_20 : {}}>
          {showGraphs ? (
            <>
              {graph && sessionMessage === true ? (
                <TrialByTrialProgressGraph
                  program={program}
                  data={
                    useObservables ? analyzeSets(currentTarget) : programData
                  }
                  sets={recentSetsByTargetId?.[currentTarget] || []}
                  sessions={programSessions}
                  session={session}
                  allSets={recentSets}
                  recentSetsByTargetId={recentSetsByTargetId}
                  envs={envs}
                  filteredEnvItems={filteredEnvItems}
                  title={title}
                  extras={extras}
                  startDate={startDate}
                  endDate={endDate}
                  byDay={byDay}
                  shouldShowSummary={shouldShowSummary}
                  summaryOnly={summaryOnly}
                  targets={targets}
                  shouldShowMenu={shouldShowMenu}
                  sessionMessage={sessionMessage}
                  target={targetById?.[currentTarget]?.[0]}>
                  {!summaryOnly ? (
                    <SelectInput
                      label={'Target'}
                      items={dropdownTargets}
                      onChange={setCurrentTarget}
                      value={currentTarget}
                      error={undefined}
                    />
                  ) : null}
                </TrialByTrialProgressGraph>
              ) : programData ? (
                <TrialByTrialProgressGraph
                  program={program}
                  data={programData}
                  sets={recentSetsByTargetId?.[currentTarget] || []}
                  sessions={programSessions}
                  allSets={filteredSets}
                  events={programEvents}
                  recentSetsByTargetId={recentSetsByTargetId}
                  envs={envs}
                  filteredEnvItems={filteredEnvItems}
                  title={title}
                  extras={extras}
                  startDate={startDate}
                  endDate={endDate}
                  byDay={byDay}
                  shouldShowSummary={shouldShowSummary}
                  summaryOnly={summaryOnly}
                  targets={targets}
                  reportScreen={reportScreen}
                  shouldShowMenu={shouldShowMenu}
                  target={targetById?.[currentTarget]?.[0]}>
                  {!summaryOnly ? (
                    <SelectInput
                      label={'Target'}
                      items={dropdownTargets}
                      onChange={setCurrentTarget}
                      value={currentTarget}
                      error={undefined}
                    />
                  ) : null}
                </TrialByTrialProgressGraph>
              ) : null}
              {table ? (
                <>
                  {!graph ? (
                    <>
                      <SelectInput
                        label={'Target'}
                        items={dropdownTargets}
                        onChange={setCurrentTarget}
                        value={currentTarget}
                        error={undefined}
                      />
                      <RHSeparator height={16} />
                    </>
                  ) : null}

                  <View style={[styles.pt_20, styles.zIndexNegative]}>
                    {extras?.showExport ? (
                      <View
                        style={[
                          styles.paddingVertical,
                          styles.paddingHorizontal,
                          styles.alignSelfEnd,
                        ]}>
                        <Menu
                          anchor={<AntDesign size={20} name="download" />}
                          options={tableOptionsWithCsv}
                        />
                      </View>
                    ) : null}
                    {programEvents || recentEvents ? (
                      <ViewShot ref={extras?.tableExportRef}>
                        {extras?.showExport ? extras?.title : null}
                        <TrialByTrialTable
                          program={program}
                          reportScreen={reportScreen}
                          session={session}
                          useObservables={useObservables}
                          events={sessionMessage ? recentEvents : programEvents}
                          target={targetById?.[currentTarget]?.[0]}
                          sets={recentSetsByTargetId?.[currentTarget] || []}
                          startDate={startDate}
                          endDate={endDate}
                        />
                      </ViewShot>
                    ) : null}
                  </View>
                </>
              ) : null}
            </>
          ) : (
            <>
              {targets.map((_target: Target) => {
                if ([analyzeSets(_target.id)].length) {
                  return (
                    <View style={styles.pt_20} key={_target.id}>
                      <TrialByTrialTable
                        program={program}
                        events={sessionMessage ? recentEvents : events}
                        target={targetById[_target.id][0]}
                        useObservables={useObservables}
                        sets={
                          sessionMessage
                            ? recentSetsByTargetId?.[_target.id] || []
                            : setsByTargetId?.[_target.id] || []
                        }
                      />
                    </View>
                  );
                }
              })}
            </>
          )}
        </View>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  pt_20: {
    paddingTop: 20,
  },
  mt_20: {
    marginTop: 20,
  },
  flexRow: {
    flexDirection: 'row',
  },
  alignItemCenter: {
    alignItems: 'center',
  },
  justifySpaceBetween: {
    justifyContent: 'space-between',
  },
  backgroundBlack: {
    backgroundColor: Colors.RAVEN_BLACK,
  },
  backgroundTransparent: {
    backgroundColor: 'transparent',
  },
  mr_10: {
    marginRight: 10,
  },
  emptyContainer: {
    flex: 1,
    minHeight: 100,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyTitle: {
    ...Typography.P3,
    color: Colors.TEXT_PRIMARY,
    marginTop: 8,
  },
  alignSelfCenter: {
    alignSelf: 'center',
  },
  paddingVertical: {
    paddingVertical: 20,
  },
  paddingHorizontal: {
    paddingHorizontal: 20,
  },
  alignSelfEnd: {
    alignSelf: 'flex-end',
  },
  zIndexNegative: {
    zIndex: -1,
  },
});

export default TrialByTrialProgress;
